import React, { useState, useEffect } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import {
  Settings,
  UserPlus,
  Loader2,
  Upload,
  X,
  Image,
  Building,
} from 'lucide-react';

const WorkspaceSettings = () => {
  const { selectedWorkspace, refreshWorkspaces } = useWorkspace();
  const { currentUser } = useAuth();
  const [workspaceNameEdit, setWorkspaceNameEdit] = useState('');
  const [nameSaveLoading, setNameSaveLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState(null);
  const [showInviteComingSoon, setShowInviteComingSoon] = useState(false);

  const isDemo = selectedWorkspace?.isDemo;

  useEffect(() => {
    setWorkspaceNameEdit(selectedWorkspace?.name || '');
  }, [selectedWorkspace?.name]);

  const handleSaveWorkspaceName = async () => {
    const trimmed = (workspaceNameEdit || '').trim();
    if (!trimmed || !selectedWorkspace || isDemo) return;
    setNameSaveLoading(true);
    try {
      await updateDoc(doc(db, 'companies', selectedWorkspace.id), { name: trimmed });
      await refreshWorkspaces();
    } catch (error) {
      console.error('Error updating workspace name:', error);
    } finally {
      setNameSaveLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedWorkspace || isDemo) return;
    if (!file.type.startsWith('image/')) {
      setLogoError('Please upload an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setLogoError('File size should be less than 5MB');
      return;
    }
    setLogoUploading(true);
    setLogoError(null);
    try {
      const storageRef = ref(storage, `company-logos/${selectedWorkspace.id}-${Date.now()}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      await updateDoc(doc(db, 'companies', selectedWorkspace.id), { logoUrl: downloadURL });
      await refreshWorkspaces();
    } catch (error) {
      console.error('Error uploading logo:', error);
      setLogoError('Failed to upload logo. Please try again.');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!selectedWorkspace || isDemo) return;
    setLogoError(null);
    try {
      await updateDoc(doc(db, 'companies', selectedWorkspace.id), { logoUrl: '' });
      await refreshWorkspaces();
    } catch (error) {
      console.error('Error removing logo:', error);
    }
  };

  if (!currentUser) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 py-8 sm:px-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Workspace settings</h1>
        <p className="text-gray-600 mb-8">Update your company name, logo, and team.</p>

        <div className="space-y-8">
          {/* Company logo */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Image className="w-5 h-5 text-gray-500" />
              Company logo
            </h2>
            {isDemo ? (
              <p className="text-sm text-gray-500">Demo workspace logo cannot be changed.</p>
            ) : (
              <div className="flex flex-wrap items-center gap-4">
                {selectedWorkspace?.logoUrl && selectedWorkspace.logoUrl.startsWith('http') ? (
                  <div className="relative group">
                    <img
                      src={selectedWorkspace.logoUrl}
                      alt="Company logo"
                      className="w-24 h-24 object-contain rounded-xl border border-gray-200 bg-gray-50"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveLogo}
                      className="absolute -top-2 -right-2 p-1.5 bg-red-100 text-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow"
                      title="Remove logo"
                      aria-label="Remove logo"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="w-24 h-24 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-xl bg-gray-50">
                    <Image className="h-10 w-10 text-gray-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <label className="relative cursor-pointer inline-block">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      disabled={logoUploading || isDemo}
                    />
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                      {logoUploading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                          <span className="text-sm text-gray-600">Uploading...</span>
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-600">
                            {selectedWorkspace?.logoUrl ? 'Change logo' : 'Upload logo'}
                          </span>
                        </>
                      )}
                    </div>
                  </label>
                  {logoError && <p className="mt-1 text-sm text-red-600">{logoError}</p>}
                  <p className="mt-1 text-xs text-gray-500">
                    Square image recommended, max 5MB. JPG, PNG, GIF.
                  </p>
                </div>
              </div>
            )}
          </section>

          {/* Workspace name */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Building className="w-5 h-5 text-gray-500" />
              Workspace name (company name)
            </h2>
            <input
              type="text"
              value={workspaceNameEdit}
              onChange={(e) => setWorkspaceNameEdit(e.target.value)}
              disabled={isDemo}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="Company name"
            />
            {!isDemo && (
              <button
                type="button"
                onClick={handleSaveWorkspaceName}
                disabled={
                  nameSaveLoading ||
                  !(workspaceNameEdit || '').trim() ||
                  (workspaceNameEdit || '').trim() === selectedWorkspace?.name
                }
                className="mt-3 w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {nameSaveLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Save name
              </button>
            )}
            {isDemo && (
              <p className="mt-2 text-sm text-gray-500">Demo workspace name cannot be changed.</p>
            )}
          </section>

          {/* Invite members */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <button
              type="button"
              onClick={() => setShowInviteComingSoon(true)}
              className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors text-left"
            >
              <div className="p-2 bg-blue-100 rounded-lg">
                <UserPlus className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <span className="font-medium text-gray-900">Invite members</span>
                <p className="text-sm text-gray-500">Add team members to this workspace</p>
              </div>
            </button>
          </section>
        </div>
      </div>

      {/* Invite members – Coming soon modal */}
      {showInviteComingSoon && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 text-center">
            <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <UserPlus className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Invite members</h3>
            <p className="text-gray-600 mb-6">
              This feature is coming soon. You’ll be able to invite team members to your workspace.
            </p>
            <button
              type="button"
              onClick={() => setShowInviteComingSoon(false)}
              className="w-full py-2.5 px-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-medium rounded-lg"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkspaceSettings;
